import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

export function IpsPgwTab() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useQuery({
    queryKey: ["ips-pgw-settings"],
    queryFn: () => api.getIpsPgwSettings(),
    // Isti razlog kao MinimaxTab - ako modul "ips_qr" nije aktivan za firmu
    // (RequireModule guard na backendu vraća 403), nema smisla ponavljati
    // zahtev.
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"TEST" | "PRODUCTION">("TEST");
  const [tid, setTid] = useState("");
  const [userId, setUserId] = useState("");
  const [mcc, setMcc] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.ipsPgwEnabled);
      setEnvironment(settings.ipsPgwEnvironment as "TEST" | "PRODUCTION");
      setTid(settings.ipsPgwTid ?? "");
      setMcc(settings.ipsPgwMcc ?? "");
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateIpsPgwSettings({
        ipsPgwEnabled: enabled,
        ipsPgwEnvironment: environment,
        ipsPgwTid: tid || undefined,
        ipsPgwUserId: userId || undefined,
        ipsPgwMcc: mcc || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["ips-pgw-settings"], updated);
      setUserId("");
    },
  });

  const testConnection = useMutation({
    mutationFn: () => api.testIpsPgwConnection(),
  });

  if (isSettingsError) {
    if (settingsError instanceof ApiError && settingsError.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={settingsError} moduleKey="ips_qr" />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {settingsError instanceof Error ? settingsError.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">IPS QR Naplata (Payten)</h2>
        <p className="text-xs text-slate-400">
          Naplata faktura preko IPS QR koda, posredstvom Payten platnog gateway-a (PGW). Podatke
          (TID, User ID, MCC) dobijaš od banke uz aktivaciju terminala.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Aktivna IPS QR naplata za ovu firmu
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Okruženje</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as "TEST" | "PRODUCTION")}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="TEST">Test (https://ips.pgw.payten.com:9092)</option>
          <option value="PRODUCTION">Produkcija (https://ips.pgw.payten.com:5053)</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">TID (Terminal ID)</label>
          <input
            value={tid}
            onChange={(e) => setTid(e.target.value)}
            placeholder="npr. HOSTAREN"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">MCC</label>
          <input
            value={mcc}
            onChange={(e) => setMcc(e.target.value)}
            placeholder="npr. 6311"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">User ID</label>
        <input
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          type="password"
          placeholder={settings.hasIpsPgwUserId ? `${settings.ipsPgwUserIdMasked} (nepromenjeno)` : ""}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        {settings.hasIpsPgwUserId && (
          <p className="mt-1 text-xs text-slate-400">Trenutno podešen: {settings.ipsPgwUserIdMasked}</p>
        )}
      </div>

      {save.isError && save.error instanceof ApiError && save.error.response.errorCode === "MODULE_NOT_AVAILABLE" ? (
        <ModuleGateNotice error={save.error} moduleKey="ips_qr" />
      ) : (
        save.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {save.error instanceof Error ? save.error.message : "Čuvanje nije uspelo."}
          </p>
        )
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Sačuvaj
        </Button>
        <Button
          variant="secondary"
          disabled={testConnection.isPending || !settings.ipsPgwEnabled}
          onClick={() => testConnection.mutate()}
        >
          {testConnection.isPending ? "Testiram..." : "Testiraj konekciju"}
        </Button>
      </div>

      {testConnection.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {testConnection.error instanceof Error
            ? testConnection.error.message
            : "Test konekcije nije uspeo."}
        </p>
      )}
      {testConnection.isSuccess && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Konekcija uspešna - kredencijali su ispravni.
        </p>
      )}

      <p className="text-xs text-slate-400">
        Napomena: ovo za sada testira samo konekciju (generisanje tokena). Dugme "Naplati fakturu
        preko IPS QR" na fakturama dolazi u sledećem koraku.
      </p>
    </div>
  );
}
